import type { CSSProperties, ReactNode } from 'react';
import SmartLink from '@/shared/ui/SmartLink';
import ExternalLink from '@/shared/ui/ExternalLink';
import {
  SANR_DISCORD,
  SANR_TELEGRAM,
  SANR_TWITTER,
} from '@/shared/constants/links';
import helpSvg from './icons/help.svg';
import telegramSvg from './icons/telegram.svg';
import discordSvg from './icons/discord.svg';
import xSvg from './icons/x.svg';

// The design's glyph exports carry fixed fills; masking them in currentColor
// keeps every icon on the link's gray and lets dark mode recolour them.
function Glyph({ src, inset }: { src: string; inset?: string }) {
  const style: CSSProperties = {
    mask: `url(${JSON.stringify(src)}) no-repeat center / contain`,
    WebkitMask: `url(${JSON.stringify(src)}) no-repeat center / contain`,
  };
  return (
    <span aria-hidden className="relative block size-5">
      <span
        className="absolute block bg-current"
        style={{ inset: inset ?? 0, ...style }}
      />
    </span>
  );
}

const FOOTER_LINK_CLASS =
  'flex items-center gap-xs text-body-lg font-medium whitespace-nowrap text-gray hover:underline';

function FooterLink({
  icon,
  children,
  ...link
}: { icon: ReactNode; children: ReactNode } & (
  { to: '/help' } | { href: string }
)) {
  return 'href' in link ?
      <ExternalLink href={link.href} className={FOOTER_LINK_CLASS}>
        {icon}
        {children}
      </ExternalLink>
    : <SmartLink to={link.to} className={FOOTER_LINK_CLASS}>
        {icon}
        {children}
      </SmartLink>;
}

// The ≤767 bottom padding is the bottom-nav pill's clearance (the 72px pill
// zone plus a gutter), so pages carry none of their own.
export default function Footer() {
  return (
    <footer className="flex w-full flex-col items-center gap-3xl border-t border-gray-5 px-27 pt-4xl pb-5xl max-md:px-l max-md:pt-2xl max-md:pb-25">
      <div className="flex w-full flex-col items-center gap-l text-body-md font-medium">
        <p className="text-center text-gray-text">
          Score is an application that incentivizes high quality price
          predictions by leveraging blockchain technology, a native reputation
          system, and perpetual competitions.
        </p>
        <p className="text-center text-gray">
          powered by{' '}
          <ExternalLink
            className="inline-block text-inherit hover:underline"
            href="https://santiment.net/"
          >
            Santiment
          </ExternalLink>{' '}
          • © 2026. All rights reserved.
        </p>
      </div>
      <div className="flex items-start gap-5xl max-md:w-full max-md:flex-col max-md:gap-2xl">
        <div className="flex items-start gap-3xl">
          <FooterLink to="/help" icon={<Glyph src={helpSvg} inset="5%" />}>
            Help & Support
          </FooterLink>
        </div>
        <div className="h-6 w-px bg-gray-5 max-md:hidden" />
        <div className="flex items-start gap-3xl">
          <FooterLink
            href={SANR_TELEGRAM}
            icon={<Glyph src={telegramSvg} inset="20% 18.33% 24.81% 15%" />}
          >
            Telegram
          </FooterLink>
          <FooterLink
            href={SANR_DISCORD}
            icon={<Glyph src={discordSvg} inset="20% 10%" />}
          >
            Discord
          </FooterLink>
          <FooterLink href={SANR_TWITTER} icon={<Glyph src={xSvg} />}>
            X
          </FooterLink>
        </div>
      </div>
    </footer>
  );
}
